import { CnpjValidationError } from '../models/validation-errors.model';

/**
 * Validates a Brazilian CNPJ number with reason-specific error codes.
 * @param value - CNPJ string (with or without formatting)
 * @returns ValidationErrors with reason code or null if valid
 */
export function validateCnpj(value: string): CnpjValidationError | null {
  if (!value) return null;

  // Remove formatting (dots, slashes, and dashes)
  const cnpj = value.replace(/\D/g, '');

  // Check length
  if (cnpj.length !== 14) {
    return { cnpj: { reason: 'length', expected: 14, actual: cnpj.length } };
  }

  // Check for all same digits
  if (/^(\d)\1+$/.test(cnpj)) {
    return { cnpj: { reason: 'allSameDigits' } };
  }

  // First check digit weights: 5,4,3,2,9,8,7,6,5,4,3,2
  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj[i], 10) * firstWeights[i];
  }
  let remainder = sum % 11;
  const firstDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cnpj[12], 10) !== firstDigit) {
    return { cnpj: { reason: 'checksumFirstDigit' } };
  }

  // Second check digit weights: 6,5,4,3,2,9,8,7,6,5,4,3,2
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj[i], 10) * secondWeights[i];
  }
  remainder = sum % 11;
  const secondDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cnpj[13], 10) !== secondDigit) {
    return { cnpj: { reason: 'checksumSecondDigit' } };
  }

  return null;
}
